import get from 'lodash-es/get';
import sortBy from 'lodash-es/sortBy';
import filter from 'lodash-es/filter';
import isEqual from 'lodash-es/isEqual.js';
import isEmpty from 'lodash-es/isEmpty.js';
import * as firestoreRedux from '@dreamworld/firestore-redux';
import { createSelector } from 'reselect';
import * as searchSelectors from '../search/selectors.js';

/**
 * @property {Object} state Redux state
 * @property {String} cardId - Card Id
 * @return { Array } List of card chats sorted by createdAt.
 */
export const cardChats = createSelector(
  ({ state, cardId }) => firestoreRedux.selectors.queryStatus(state, `card-chats-${cardId}`),
  ({ state, cardId }) => firestoreRedux.selectors.docsByQueryResult(state, `card-chats-${cardId}`, 'card-chat-messages'),
  (queryStatus, chats) => {
    if (!queryStatus || (isEmpty(chats) && queryStatus === 'PENDING')) {
      return;
    }
    return sortBy(chats, ['createdAt']);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 200,
    },
  }
);

/**
 * @property {Object} state Redux state
 * @property {String} boardId - Board Id.
 * @return { Array } List of board chats sorted by createdAt.
 */
export const boardChats = createSelector(
  ({ boardId }) => boardId,
  ({ state, boardId }) => firestoreRedux.selectors.queryStatus(state, `board-chats-${boardId}`),
  ({ state, boardId }) => firestoreRedux.selectors.queryStatus(state, `board-chats-${boardId}_top`),
  ({ state, boardId }) => firestoreRedux.selectors.queryStatus(state, `board-chats-${boardId}_bottom`),
  ({ state, boardId }) => firestoreRedux.selectors.docsByQueryResult(state, `board-chats-${boardId}`, 'board-chat-messages'),
  ({ state }) => firestoreRedux.selectors.collection(state, 'board-chat-messages'),
  ({ state }) => searchSelectors.highlightedChats(state),
  (boardId, queryStatus, queryStatusTop, queryStatusBottom, currentBoardChats, allBoardChats, highlightedChats) => {
    const chats = isEmpty(highlightedChats) ? currentBoardChats : filter(allBoardChats, { boardId });
    queryStatus = isEmpty(highlightedChats) ? queryStatus: queryStatusTop === 'PENDING' || queryStatusBottom === 'PENDING' ? 'PENDING': 'LIVE';
    if (!queryStatus || (isEmpty(chats) && queryStatus === 'PENDING')) {
      return;
    }

    return sortBy(chats, ['createdAt']);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * @returns `true` when all bottom board chats are loaded.
 */
export const allTopBoardChatsLoaded = (state) => get(state, `chat.allTopBoardChatsLoaded`);

/**
 * @returns `true` when all top board chats are loaded.
 */
export const allBottomBoardChatsLoaded = (state) => get(state, `chat.allBottomBoardChatsLoaded`);

/**
 * @param {Object} state Redux state.
 * @param {String} chatId Chat ID passed to give chat item model.
 * @return { Object } Card chat model.
 */
export const cardChatItem = (state, chatId) => {
  return firestoreRedux.selectors.doc(state, 'card-chat-messages', chatId);
};

/**
 * @param {Object} state Redux state.
 * @param {String} chatId Chat ID passed to give chat item model.
 * @return { Object } Selector which return chat model.
 */
export const boardChatItem = (state, chatId) => {
  return firestoreRedux.selectors.doc(state, 'board-chat-messages', chatId);
};

/**
 * given chat board id.
 */
export const chatBoardId = (state, chatId) => {
  const chat = chatId && chatId.startsWith('ccm_') ? cardChatItem(state, chatId): boardChatItem(state, chatId);
  return chat && chat.boardId;
}

/**
 * @param {Object} state 
 * @param {String} containerId 
 * @returns {String} given container add message status.
 */
export const addNewChatStatus = (state, containerId) => get(state, `chat.addNewChat.${containerId}.status`);

/**
 * @param {Object} state Redux state.
 * @param {String} containerId Card id for a card chat, board id for a board chat.
 * @returns {String} What the user has typed into the chat box and not sent yet.
 */
export const newChatDraft = (state, containerId) => get(state, `chat.newChatDrafts.${containerId}`, '');

/**
 * @param {Object} state Redux state.
 * @param {String} chatMessageId Id of the message being edited.
 * @returns {String} The edit the user has started on that message and not sent yet.
 */
export const editDraft = (state, chatMessageId) => get(state, `chat.editDrafts.${chatMessageId}`, '');

/**
 * @param {Object} state Redux state.
 * @returns {String|undefined} Id of the posted message open for editing in this tab.
 */
export const openEditId = (state) => get(state, 'chat.openEditId');
